<template>
  <header
    class="header"
    :class="{
      'mobile-view': isUserAuthorized,
      'with-announcements': hasAnyAnnouncements,
    }"
  >
    <div
      class="container"
      :class="{ container__marketing: !isUserAuthorized }"
    >
      <div class="container-content mobile-view">
        <div
          v-if="isUserAuthorized"
          class="mobile-filters mobile-only"
        >
          <router-link
            v-if="(showBackButton || showMyAcvHeader) && isUserAuthorized"
            type="button"
            class="btn back-btn"
            :to="backButtonRoute"
            @click="trackLinkClicked"
          >
            <span class="tw-sr-only">
              Back
            </span>
            <acv-icon
              icon="chevron-left"
            />
          </router-link>
        </div>
        <div
          :class="{
            'container-content center-logo': isUserAuthorized,
            'container-content-unauthorized': !isUserAuthorized,
          }"
        >
          <div
            class="header__logo"
          >
            <div
              v-if="showMyAcvHeader"
              class="mobile-page-title"
            >
              {{ myAcvHeaderText }}
            </div>
            <component
              :is="logoComponent"
              v-else
              :href="logoUrl"
              :to="logoUrl"
              @[logoComponentClickEventName]="handleLogoClicked"
            >
              <component
                :is="headerLogoComponent"
                class="acv-logo"
                :class="{ 'has-filter-icon': showFilterIcon && isUserAuthorized }"
                alt="The logo of ACV Auctions"
              />
            </component>
          </div>
          <nav
            :class="{
              'tablet-hidden header__nav--tight': !isUserAuthorized,
              'mobile-hidden lighter': isUserAuthorized,
            }"
            class="header__nav"
          >
            <ul>
              <li
                v-for="(route, index) in routes"
                :key="index"
                :class="route.text.replace(/\W+/g, '-').toLowerCase()"
                class="header-link"
              >
                <router-link
                  v-if="route.path && !route.path.includes('http')"
                  :key="route.path"
                  :to="route.path"
                  :class="{ active: isActivePath(route.path) }"
                  @click="trackLinkClicked"
                >
                  {{ route.text }}
                </router-link>
                <acv-legacy-dropdown
                  v-else-if="route.childRoutes"
                  :ref="`${index}_menu`"
                  class="has__child__routes"
                >
                  <template #trigger="{ toggle }">
                    <button
                      type="button"
                      class="has__child__routes__toggle"
                      @click="toggle"
                    >
                      {{ route.text }}
                    </button>
                  </template>
                  <a
                    v-for="(childRoute, childRouteIndex) in route.childRoutes"
                    :key="childRouteIndex"
                    class="dropdown-item"
                    :href="childRoute.path"
                    @click="trackLinkClicked"
                  >
                    {{ childRoute.text }}
                  </a>
                </acv-legacy-dropdown>
                <a
                  v-else
                  :href="route.path"
                  @click="trackLinkClicked"
                >
                  <span
                    v-if="route.smallText"
                  >
                    <span class="tablet-hidden fullwidth-hidden">
                      {{ route.smallText }}
                    </span>
                    <span class="fullwidth-only">
                      {{ route.text }}
                    </span>
                  </span>
                  <span
                    v-else
                  >
                    {{ route.text }}
                  </span>
                </a>
                <span
                  v-if="route.badgeText"
                  class="nav-badge"
                >
                  {{ route.badgeText }}
                </span>
              </li>
            </ul>
          </nav>
        </div>
        <div
          class="container-content__button-block"
          :class="{
            'container-content__button-block-unauthorized': !isUserAuthorized,
          }"
        >
          <nav
            v-if="isUserAuthorized"
            class="header__buttons mobile-hidden"
          >
            <ul>
              <li
                class="notification-icon"
              >
                <acv-notifications-menu />
              </li>
              <li class="settings-cog">
                <acv-settings-dropdown-menu />
              </li>
            </ul>
          </nav>
          <nav
            v-if="!isUserAuthorized"
            class="tablet-hidden"
          >
            <ul
              style="padding-left: 0;"
            >
              <li
                v-if="!isUserAuthorized"
                class="button__wrapper"
              >
                <router-link
                  to="/login"
                  custom
                >
                  <template #default="{ navigate }">
                    <button
                      type="button"
                      class="login__button"
                      @click="(event) => { trackLinkClicked(); navigate(event); }"
                    >
                      LOG IN
                    </button>
                  </template>
                </router-link>
              </li>
              <li
                v-if="!isUserAuthorized"
                class="button__wrapper"
              >
                <router-link
                  to="/registration"
                  custom
                >
                  <template #default="{ navigate }">
                    <button
                      type="button"
                      class="button signup__button"
                      @click="(event) => { trackLinkClicked(); navigate(event); }"
                    >
                      REGISTER NOW
                    </button>
                  </template>
                </router-link>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
    <div
      v-if="showEnvironmentName"
      class="environment-alert"
    >
      {{ `YOU ARE CURRENTLY VIEWING ENVIRONMENT: ${environmentName.toUpperCase()}` }}
    </div>
  </header>
</template>

<script>
import { defineAsyncComponent, defineComponent, nextTick } from 'vue';
import { mapState, mapGetters } from 'vuex';
import { AcvAuctionsPrimaryDarkSvg, AcvAuctionsPrimaryLightSvg } from '@acv/shared-vuejs';
import AcvLegacyDropdown from '@/components/Inputs/AcvLegacyDropdown.vue';
import AcvSettingsDropdownMenu from '@/components/SettingsDropdownMenu.vue';
import AcvNotificationsMenu from '@/components/Notifications/NotificationsMenu.vue';
import FeatureFlags from '@/mixins/FeatureFlags';
import links from '@/constants/links';
import { postRedirect } from '@/utilities';
import Analytics from '@/mixins/Analytics';
import ToastMessages from '@/mixins/ToastMessages';

export default defineComponent({
  name: 'AcvHeader',
  components: {
    AcvAuctionsPrimaryDarkSvg,
    AcvAuctionsPrimaryLightSvg,
    AcvSettingsDropdownMenu,
    AcvNotificationsMenu,
    AcvLegacyDropdown,
    AcvIcon: defineAsyncComponent(() => import('@acv/shared-vuejs').then((shared) => shared.AcvIcon)),
  },
  mixins: [
    Analytics,
    FeatureFlags,
    ToastMessages,
  ],
  props: {
    backButtonRoute: {
      type: String,
      default: '/auctions/active',
    },
    dealershipName: {
      type: String,
      default: '',
    },
    currentUser: {
      type: Object,
      required: true,
    },
    showFilterIcon: {
      type: Boolean,
      default: false,
    },
    showBackButton: {
      type: Boolean,
      default: false,
    },
    canSell: {
      type: Boolean,
      default: false,
    },
    numberOfActiveFilters: {
      type: Number,
      default: 0,
    },
  },
  data: () => ({
    links,
    isInMobileView: false,
    mediaQueryList: null,
  }),
  computed: {
    ...mapState({
      route: (state) => state.route,
    }),
    ...mapGetters({
      isLimitedUser: 'isLimitedUser',
      showCapitalMenu: 'showCapitalMenu',
      showingEventsDirectory: 'marketplaceView/showingEventsDirectory',
      hasMobileAnnouncements: 'announcements/hasMobileAnnouncements',
      hasDesktopAnnouncements: 'announcements/hasDesktopAnnouncements',
    }),
    hasAnyAnnouncements() {
      return (this.isInMobileView && this.hasMobileAnnouncements) || this.hasDesktopAnnouncements;
    },
    logoComponent() {
      return this.isAuthorized ? 'router-link' : 'a';
    },
    logoComponentClickEventName() {
      return 'click';
    },
    logoUrl() {
      return this.isUserAuthorized
        ? '/marketplace'
        : this.marketingSiteUrl;
    },
    headerLogoComponent() {
      return this.isUserAuthorized
        ? 'AcvAuctionsPrimaryLightSvg'
        : 'AcvAuctionsPrimaryDarkSvg';
    },
    isUserAuthorized() {
      return this.isAuthorized && !this.$route.path.includes('/registration');
    },
    canUserSell() {
      return this.canSell;
    },
    email() {
      return this.currentUser.email;
    },
    isAdmin() {
      return this.currentUser.isAdmin;
    },
    currentUserFullName() {
      return this.currentUser.fullName;
    },
    isUserAuthorizedAndCanSell() {
      return this.isUserAuthorized && this.canUserSell;
    },
    isUserAuthorizedAndCanNotSell() {
      return this.isUserAuthorized && !this.canUserSell;
    },
    marketingSiteUrl() {
      return import.meta.env.VITE_MARKETING_SITE;
    },
    showPaymentSettings() {
      return this.featureFlags[this.featureFlagSlugs.showPaymentSettings];
    },
    enableSrpEventDirectory() {
      return this.featureFlags[this.featureFlagSlugs.showSrpEventDirectory];
    },
    routes() {
      const routes = [
        {
          path: '/marketplace',
          text: 'Marketplace',
          cssClasses: ['marketplace-header-link'],
          display: this.isUserAuthorized,
          key: 'marketplace',
          dropdownItems: this.enableSrpEventDirectory
            ? [
              {
                label: 'Search',
                event: {
                  key: 'marketplace',
                },
              },
              {
                label: 'Events Directory',
                event: {
                  key: 'directory',
                },
              },
            ]
            : [],
        },
        {
          path: '/myacv',
          text: 'My ACV',
          cssClasses: ['myacv-header-link'],
          display: this.isUserAuthorized && !this.isLimitedUser,
        },
        {
          path: '/market-report',
          text: 'Market Report',
          cssClasses: ['marketreport-header-link'],
          display: this.isUserAuthorized,
        },
        {
          path: '/capital',
          text: 'ACV Capital',
          display: this.isUserAuthorized && this.showCapitalMenu,
        },
        {
          path: import.meta.env.VITE_BUYING_MATRIX_BASE_URI,
          text: links.buyingSystem.title,
          display: this.showBuyingMatrix,
          click: this.handleBuyingMatrixLinkClicked,
        },
        {
          path: '',
          text: links.clearCarAppraisal.title,
          display: this.showClearCarAppraisalTab,
          click: this.handleClearCarAppraisalLinkClicked,
        },
        {
          path: links.marketingSite.features.url,
          text: links.marketingSite.features.text,
          display: !this.isUserAuthorized,
        },
        {
          path: links.marketingSite.testimonials.url,
          text: links.marketingSite.testimonials.text,
          smallText: links.marketingSite.testimonials.smallText,
          display: !this.isUserAuthorized,
        },
        {
          path: links.marketingSite.pricing.url,
          text: links.marketingSite.pricing.text,
          display: !this.isUserAuthorized,
        },
        {
          path: links.marketingSite.sell.url,
          text: links.marketingSite.sell.text,
          smallText: links.marketingSite.sell.smallText,
          display: !this.isUserAuthorized,
        },
        {
          path: links.marketingSite.careers.url,
          text: links.marketingSite.careers.text,
          smallText: links.marketingSite.careers.smallText,
          display: !this.isUserAuthorized,
        },
        {
          path: links.marketingSite.contact.url,
          text: links.marketingSite.contact.text,
          display: !this.isUserAuthorized,
        },
        {
          path: '/transportation-hub',
          text: 'ACV Transport',
          display: this.isUserAuthorized,
        },
      ];
      return routes.filter((route) => route.display);
    },
    showEnvironmentName() {
      const hideEnvironmentNameInEnvironments = [
        'production',
        'development',
      ];
      return !hideEnvironmentNameInEnvironments
        .includes(this.environmentName);
    },
    environmentName() {
      return import.meta.env.VITE_ENV_NAME || 'UNKNOWN (DID YOU FORGET TO ADD AN ENVIRONMENT NAME TO THE ENV FILE?)';
    },
    isMyAcvRoute() {
      return this.$route.path.includes('myacv');
    },
    showMyAcvHeader() {
      return this.isMyAcvRoute && this.$route.name !== 'MyACV' && this.isInMobileView;
    },
    myAcvHeaderText() {
      if (this.$route.matched.length > 2) {
        const parentIndex = this.$route.matched.length - 2;
        return this.$route.matched[parentIndex].meta.shortTitle;
      }

      return this.$route.meta.shortTitle;
    },
    showBuyingMatrix() {
      return this.isUserAuthorized
          && this.featureFlags[this.featureFlagSlugs.buyingMatrixLink];
    },
    showClearCarAppraisalTab() {
      return this.isUserAuthorized
          && this.featureFlags[this.featureFlagSlugs.showClearCarAppraisalTab];
    },
  },
  created() {
    this.mediaQueryList = window.matchMedia('(max-width: 767px)');
    this.mediaQueryList.addListener(this.handleResize);
    this.handleResize(this.mediaQueryList);
  },
  unmounted() {
    this.mediaQueryList.removeListener(this.handleResize);
  },
  methods: {
    isActivePath(path) {
      return this.$route.path === path || this.$route.path.startsWith(`${path}/`);
    },
    trackLinkClicked() {
      if (this.showingEventsDirectory) {
        this.$store.dispatch('marketplaceView/updateShowingEventsDirectory', '');
      }
      nextTick(() => {
        this.track(this.eventNames.headerLinkClicked, {
          to_route: this.route.path,
          from_route: this.route.from.path,
        });
      });
    },
    handleLogoClicked() {
      this.trackLinkClicked();
    },
    handleResize(mqlEvent) {
      this.isInMobileView = mqlEvent.matches;
    },
    async handleBuyingMatrixLinkClicked() {
      this.track(this.eventNames.buyingSystemLinkClicked);
      if (this.isAuthorized) {
        const { jwtToken } = await this.$store.dispatch('getJwtTokens');
        window.location = `${links.buyingSystem.url}${jwtToken}`;
      }
    },
    async handleClearCarAppraisalLinkClicked() {
      this.track(this.eventNames.clearCarAppraisalLinkClicked);
      if (this.isAuthorized) {
        try {
          const extendedInfoToken = await this.$store.dispatch('getExtendedInfoToken');
          postRedirect(links.clearCarAppraisal.url, { token: extendedInfoToken });
        } catch {
          this.showErrorToast('An error has occurred while trying to access Appraisal. Please try again.');
        }
      }
    },
  },
});
</script>

<style scoped lang="scss">
.header {
  position: fixed;
  z-index: 6;
  top: 0;
  width: 100%;
  height: 73px;
  background: #272727;

  &.with-announcements {
    position: relative;
  }

  &.mobile-view {
    @apply tw-bg-white;

    border-bottom: 1px solid #ddd;

    .filter-btn,
    .back-btn {
      color: theme('colors.black.500');

      .acv-icon {
        font-size: 36px;
      }

      &:hover {
        color: theme('colors.black.670');
      }
    }

    .mobile-page-title {
      color: theme('colors.black.670');
    }
  }

  &__logo {
    display: inline-block;
    margin-right: 30px;

    a {
      display: block;
      height: 73px;
    }

    .acv-logo {
      height: 43px;
      margin-top: 15px;
    }
  }

  nav {
    ul {
      margin: auto;

      li {
        display: inline-block;
        margin-right: 30px;
        font-weight: 300;
        color: rgb(255 255 255 / 80%);

        .header-link {
          &:hover,
          &.active {
            color: constants.$orange;
            text-decoration: none;
          }

          &:hover {
            cursor: pointer;
          }

          &.active {
            &:hover {
              cursor: default;
            }
          }
        }

        .nav-badge {
          @apply tw-bg-error-base
        tw-text-white
          tw-py-1
          tw-px-2
          tw-ml-1.5
          tw-relative
          tw-bottom-0.5;

          border-radius: 60px;
          font-size: 10px;
        }

        @include constants.md {
          margin-right: 20px;
        }

        ::v-deep .has__child__routes .acv-legacy-dropdown__panel {
          border-radius: 0;
          font-size: 14px;

          .dropdown-item {
            display: block;
            height: 40px;
            line-height: 20px;
            padding: 10px 15px;
          }
        }

        ::v-deep button {
          font-size: 14px;
          color: rgb(255 255 255 / 80%);
          border: none;
          background-color: transparent;

          &:hover {
            color: constants.$orange;
            border: none;
            background-color: transparent;
          }

          &:active,
          &:focus {
            border: none;
            background-color: transparent;
          }
        }

        a {
          color: rgb(255 255 255 / 80%);

          &:hover,
          &.active {
            color: constants.$orange;
            text-decoration: none;
          }
        }

        &.button__wrapper {
          margin-right: 8px;

          ::v-deep button {
            @include constants.border-radius(2px);

            margin-top: 13px;
            line-height: 40px;
            padding: 0 24px;
            font-family: constants.$roboto;
            font-weight: 900;
            cursor: pointer;

            &.login__button {
              margin-right: -4px;
              border: solid 1px rgba(#fff, 0);
              background: none;
              color: rgba(#fff, 0.87);

              &:hover {
                color: white;
                border: solid 1px rgba(#fff, 0.6);
              }
            }

            &.signup__button {
              background: constants.$orange;
              color: white;
              text-shadow: 1px 1px rgb(0 0 0 / 20%);
              border-color: solid 1px constants.$orange;

              &:hover {
                background: color.adjust(constants.$orange, $blackness: 5%);
                color: white;
                border-color: solid 1px constants.$orange;
              }
            }
          }
        }

        &.button {
          margin-right: 5px;
          font-family: constants.$montserrat;
          font-weight: bold;
          text-transform: uppercase;

          .btn {
            width: 85px;
            text-transform: uppercase;
          }
        }

        &.settings-cog {
          color: rgb(255 255 255 / 80%);
          font-size: 22px;

          &:hover {
            i,
            svg {
              color: white !important;
            }
          }
        }

        &.notification-icon {
          color: rgb(255 255 255 / 40%);
          font-size: 22px;
          margin-right: 18px;
          position: relative;

          &:hover {
            i,
            svg {
              color: white;
            }
          }

          span.notification-badge {
            @include constants.notification-bubble;

            top: 19px;
            right: -8px;
          }
        }

        &.settings-cog,
        &.notification-icon {
          ::v-deep .btn {
            padding: 0;
          }
        }

        &:last-of-type {
          margin-right: 0;
        }
      }
    }
  }

  .header__nav,
  .header__buttons {
    display: inline-block;
    font-family: constants.$roboto;
    vertical-align: top;
    transform: translate(0, 0);

    ul {
      display: inline-block;
      margin-bottom: 0;
      padding-left: 0;
      font-size: 15px;
      vertical-align: top;
      list-style-type: none;

      li {
        display: inline-block;
        height: 73px;
        margin-right: 30px;
        font-weight: 500;
        line-height: 72px;
        vertical-align: top;

        @media (min-width: constants.$sm-max) and (max-width: constants.$lg-min) {
          margin-right: 25px;
        }

        @media (min-width: constants.$xs-max) and (max-width: constants.$sm-max) {
          margin-right: 15px;
        }

        &.header__nav--home-link,
        &.header__nav--sell-link {
          display: none;
        }

        a,
        button {
          display: inline-block;
          color: rgba(#fff, 0.87);
          font-size: 15px;

          &:hover,
          &.active,
          &:active,
          &:focus,
          &.router-link-active {
            color: constants.$orange;
            text-decoration: none;
          }

          &:not(.marketing-btn) {
            height: 73px;
          }
        }

        &.button {
          margin-right: 5px;

          .marketing-btn {
            width: auto;
            padding-right: 20px;
            padding-left: 20px;
            text-transform: uppercase;

            &.marketing-btn--orange,
            &.marketing-btn--login {
              position: relative;
              top: -1px;
              padding: 0 23px;
              font-size: 14px;

              &:hover,
              &:focus {
                color: white;
              }

              span {
                position: relative;
                top: -1px;

                span {
                  top: 0;
                }
              }
            }

            &.marketing-btn--ghost {
              position: relative;
              top: -1px;
              padding: 0 23px;
              font-size: 14px;
            }
          }
        }

        &:last-of-type {
          margin-right: 0;
        }
      }
    }

    &.lighter {
      ul {
        li {
          @media (max-width: constants.$sm-max) {
            margin-right: 12px;
          }

          a {
            font-size: 14px;
            font-weight: 300;
          }
        }
      }
    }

    &--tight {
      ul {
        li {
          @media (max-width: constants.$lg-min) {
            margin-right: 30px;
          }
        }
      }
    }
  }

  .mobile-hidden {
    @media (max-width: constants.$xs-max) {
      display: none;
    }
  }

  .tablet-hidden {
    @media (max-width: constants.$sm-max) {
      display: none;
    }
  }

  .tablet-only {
    @media (min-width: constants.$md-min) {
      display: none;
    }
  }

  .fullwidth-hidden {
    @media (min-width: constants.$lg-min) {
      display: none;
    }
  }

  .fullwidth-only {
    @media (max-width: constants.$md-max) {
      display: none;
    }
  }
}

.filter-btn,
%filter-btn {
  // NOTE: -webkit prefix needed; See: https://acvauctions.atlassian.net/browse/VUE-273 -RT
  appearance: none;
  position: relative;
  font-size: 16px;
  border: none;
  background: transparent;
  color: #fafafa;
  margin: 17px 0 0 -11px;

  i,
  svg {
    margin-right: 7px;
  }

  &:hover {
    background: transparent;
  }

  &:focus {
    box-shadow: none;
  }

  .active-filters-badge {
    top: 14px;
    right: -14px;
    position: absolute;
    font-weight: bold;
    text-decoration: none;
    z-index: 1110;
    font-size: 12px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: constants.$orange;
    color: white;
    border-radius: 50%;
    margin: 0;
    padding: 0;
    height: 22px;
    width: 22px;
  }

  @include constants.xs {
    width: 32px;
    height: 73px;
    margin: 0;
    padding: 0;
    display: inline-flex;
    justify-content: center;
    align-items: center;

    i,
    svg {
      margin-right: 0;
      transform: none;
    }

    &:hover {
      background: none;
      color: white;
    }
  }
}

.back-btn {
  @extend %filter-btn;

  font-size: 20px;
}

.center-logo {
  @media (max-width: constants.$xs-max) {
    text-align: center;

    .header__logo {
      margin-right: 0;
    }
  }
}

.mobile-only {
  @media (min-width: constants.$sm-min) {
    display: none;
  }
}

.mobile-filters {
  flex: 0 0 25%;
  min-width: 55px;
}

.container__marketing {
  @media (max-width: constants.$sm-max) {
    width: auto;
    max-width: 1170px;
  }
}

.container {
  .container-content {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    &.center-logo {
      justify-content: center;
    }

    &.mobile-view {
      display: grid;
      width: 100%;
      grid-template-columns: repeat(3, 1fr);

      @screen tw-md {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
      }
    }

    &__button-block {
      flex: 0 0 9%;
      max-width: 60px;
      padding: 0;
      text-align: right;

      &-unauthorized {
        flex: 0 0 30%;
        max-width: 250px;

        @media (max-width: constants.$sm-max) {
          display: none;
        }
      }

      @media (max-width: constants.$xs-max) {
        display: none;
      }
    }
  }
}

.environment-alert {
  height: 15px;
  font-weight: bolder;
  font-size: 0.75em;
  width: 100%;
  color: #fff;
  background-color: constants.$orange;
  text-align: center;
  margin-right: 0;
  margin-left: 0;
  position: fixed;
  top: 65.5px;
  display: block;
  z-index: -1;
}

.mobile-page-title {
  font-weight: 500;

  // color: rgb(white 0.87);
  line-height: 73px;
  font-size: 15px;
  white-space: nowrap;
}
</style>
